import { apiInstance } from "api/config/axios";
import { IApiResponse } from "api/models/IApiResponse";
import { LessonFlat } from "models/LessonFlat";
import { getApiResponseData } from "api/services/api-service";
import { IUpdateLessonTagRequest } from "api/models/request/IUpdateLessonTagRequest";
import { IGetMyTrainingStatisticsResponse } from "api/models/response/IGetMyTrainingStatisticsResponse";
import { UserTrainingStatistics } from "models/UserTrainingStatistics";
import { IGetUserTrainingInfoRequest } from "api/models/request/IGetUserTrainingInfoRequest";
import { IDeleteLessonRequest } from "api/models/request/IDeleteLessonRequest";
import { IEditLessonRequest } from "api/models/request/IEditLessonRequest";
import { IDeleteManyLessonsRequest } from "api/models/request/IDeleteManyLessonsRequest";
import { LiveTrainingData } from "models/LiveTrainingData";
import { LessonsTags } from "models/LessonsTags";
import { IUpdateLessonImageRequest } from "api/models/request/IUpdateLessonImageRequest";
import { IGetTrainingWithVersionsAndProgressRequest } from "api/models/request/IGetTrainingWithVersionsAndProgressRequest";
import { IDeleteUserLessonsRequest } from "api/models/request/IDeleteUserLessonsRequest";
import { IGetLessonImageRequest } from "api/models/request/IGetLessonImageRequest";
import { IGetLessonVersionRequest } from "api/models/request/IGetLessonVersionRequest";
import { IGetLessonWithVersionsResponse } from "api/models/response/IGetLessonWithVersionsResponse";
import { ISetDefaultVersionRequest } from "api/models/request/ISetDefaultVersionRequest";
import { IDeleteLessonVersionRequest } from "api/models/request/IDeleteLessonVersionRequest";
import { IStartTrainingForUsersRequest } from "api/models/request/IStartTrainingForUsersRequest";
import { IGenerateTrainingReportRequest } from "api/models/request/IGenerateTrainingReportRequest";
const lessonApiBaseUrl = '/Lesson';

export const getLessons = (request: { workspaceId: string }): Promise<IApiResponse<LessonFlat[]>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/all/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getMyTrainingStatistics = (request: IGetUserTrainingInfoRequest & { workspaceId: string }): Promise<IApiResponse<IGetMyTrainingStatisticsResponse>> => {
    const url = request.userId ? `${lessonApiBaseUrl}/training-info/workspaces/${request.workspaceId}/users/${request.userId}` : `${lessonApiBaseUrl}/my-training-info/workspaces/${request.workspaceId}`;
    return apiInstance.get(url).then((response) => {
        return getApiResponseData(response);
    });
};

export const getStatistics = (request: { workspaceId: string }): Promise<IApiResponse<UserTrainingStatistics[]>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/statistics/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getStatisticsWithArchives = (
    workspaceId: string,
    request: { userIds: string[]; lessonIds: string[] },
): Promise<IApiResponse<{ blob: Blob; fileName: string }>> => {
    return apiInstance
        .post<Blob>(
            `${lessonApiBaseUrl}/statistics/get-with-archives/workspaces/${workspaceId}`,
            { userIds: request.userIds, lessonIds: request.lessonIds },
            { responseType: 'blob' },
        )
        .then((response) => ({
            success: true,
            data: {
                blob: response.data,
                fileName: `training-statistics-${Date.now()}.csv`,
            },
            status: response.status,
            message: response.statusText,
            errors: undefined,
        }))
        .catch((error) => ({
            success: false,
            data: undefined,
            status: error.response?.status || 500,
            message: 'statistics.archiveReportError',
            errors: error.response?.data?.errors || [{ code: "UNKNOWN_ERROR", message: "Unexpected error" }],
        }));
};

export const generateCsvCertificate = (request: IGenerateTrainingReportRequest & { workspaceId: string }): Promise<IApiResponse<{ blob: Blob; fileName: string }>> => {
    return apiInstance
        .post<Blob>(
            `${lessonApiBaseUrl}/certificate-csv/workspaces/${request.workspaceId}`,
            { userIds: request.userIds, lessonId: request.lessonId },
            { responseType: 'blob' },
        )
        .then((response) => ({
            success: true,
            data: {
                blob: response.data,
                fileName: `training-certificate-${new Date()
                    .toISOString()
                    .replace(/[-:T]/g, '')
                    .slice(0, 14)}.csv`,
            },
            status: response.status,
            message: response.statusText,
            errors: undefined,
        }))
        .catch((error) => ({
            success: false,
            data: undefined,
            status: error.response?.status || 500,
            message: 'trainingReport.error',
            errors: error.response?.data?.errors || [{ code: "UNKNOWN_ERROR", message: "Unexpected error" }],
        }));
};

export const getUsersWithLiveTrainingInfo = (request: { userIds: string[]; lessonIds: string[]; } & { workspaceId: string }): Promise<IApiResponse<LiveTrainingData[]>> => {
    return apiInstance.post(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/live-training-progresses`, { userIds: request.userIds, lessonIds: request.lessonIds}).then((response) => {
        return getApiResponseData(response);
    });
}

export const startTrainingForUsers = (request: IStartTrainingForUsersRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.post(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/start-many`, { userIds: request.userIds }).then((response) => {
        return getApiResponseData(response);
    });
}

export const editLesson = (request: IEditLessonRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.put(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/edit`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const getLessonImage = (request: IGetLessonImageRequest & { workspaceId: string }): Promise<IApiResponse<Blob>> => {
    return apiInstance
        .get<Blob>(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/images/${request.imageFileId}`, { responseType: 'blob' })
        .then((response) => ({
            success: true,
            data: response.data,
            status: response.status,
            message: response.statusText,
            errors: undefined,
        }))
        .catch((error) => ({
            success: false,
            data: undefined,
            status: error.response?.status || 500,
            message: error.response?.statusText || "An error occurred while fetching the lesson's photo",
            errors: error.response?.data?.errors || [{ code: "UNKNOWN_ERROR", message: "Unexpected error" }],
        }));
}

export const updateLessonImage = (request: IUpdateLessonImageRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/update-image`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const getLessonsTags = (request: {workspaceId: string}): Promise<IApiResponse<LessonsTags>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/tags/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const updateLessonTag = (request: IUpdateLessonTagRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/tag`, { newTag: request.newTag, tagType: request.tagType }).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteAssignedLessons = (request: IDeleteUserLessonsRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/users/${request.userId}/assigned-lessons`).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteLesson = (request: IDeleteLessonRequest & { workspaceId: string } ): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/delete`).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteManyLessons = (request: IDeleteManyLessonsRequest & { workspaceId: string } ): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/delete-many?lessonIds=${request.lessonIds.join(',')}`).then((response) => {
        return getApiResponseData(response);
    });
}

export const getTrainingWithVersionsAndProgress = (request: IGetTrainingWithVersionsAndProgressRequest & { workspaceId: string }): Promise<IApiResponse<string>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/active-versions-with-progress`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getLessonWithLatestVersions = (request: IGetLessonVersionRequest & { workspaceId: string }): Promise<IApiResponse<IGetLessonWithVersionsResponse>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/latest-versions`).then((response) => {
        return getApiResponseData(response);
    });
};

export const setDefaultLessonVersion = (request: ISetDefaultVersionRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/versions/${request.versionId}/set-default`, {}).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteLessonVersion = (request: IDeleteLessonVersionRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/versions/${request.language}`).then((response) => {
        return getApiResponseData(response);
    });
}